import {
  Grid,
  Modal,
  Stack,
  Typography,
  styled, Box
} from "@mui/material";
import useTheme from "@mui/material/styles/useTheme";
import { t } from "i18next";
import { Check, X } from "lucide-react";
import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import { usePreferenceLanguageMutation } from "../../queries/preferences";
import { useAuthStore, useLanguageStore } from "../../stores";
import type { Language } from "../../types";
import { persistLanguage } from "../../utils/language";
import { ButtonDigited } from "../Button/Button.variants";

const StyledModalContent = styled(Grid)(({ theme }) => ({
  background: theme.customColors.backgroundCard,
  borderRadius: theme.spacing(2),
  border: `2px solid ${theme.customColors.border}`,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
  height: "fit-content",
  margin: "auto",
  maxHeight: "80vh",
  maxWidth: "670px",
  padding: theme.spacing(4),
  width: "100%",

  ".icon": {
    cursor: "pointer"
  },

  [`${theme.breakpoints.down("sm")}`]: {
    gap: theme.spacing(2),
    padding: theme.spacing(2.5)
  }
}));

export function LanguageModal({
  close,
  languages,
  open=false
} : {
  close: () => void
  languages: Language[]
  open: boolean
}) {
  const theme = useTheme();
  const language = useLanguageStore(state => state.language);
  const setLanguage = useLanguageStore(state => state.setLanguage);
  const refetchUserInfo = useAuthStore(state => state.refetchUserInfo);
  const isAuthenticated = useAuthStore(state => Boolean(state.userData && state.userInfo));
  const { mutate: savePreferenceLanguage } = usePreferenceLanguageMutation();

  const accentColor = theme.customColors.systemSecondary01;
  const neutralColor = theme.customColors.border;

  const [selectedLanguage, setSelectedLanguage] = useState<Language | undefined>(language);
  useEffect(() => {
    language && setSelectedLanguage(language);
  }, [language]);

  const optionRefs = useRef<(HTMLDivElement | null)[]>([]);
  const hasValidSelection = languages.some((l) => l.id === selectedLanguage?.id);

  const selectAndFocusOption = (index: number) => {
    const count = languages.length;
    if (count === 0) return;
    const normalized = ((index % count) + count) % count;
    setSelectedLanguage(languages[normalized]);
    optionRefs.current[normalized]?.focus();
  };

  const handleOptionKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    index: number,
    lang: Language
  ) => {
    switch (e.key) {
    case "Enter":
    case " ":
      e.preventDefault();
      setSelectedLanguage(lang);
      break;
    case "ArrowDown":
    case "ArrowRight":
      e.preventDefault();
      selectAndFocusOption(index + 1);
      break;
    case "ArrowUp":
    case "ArrowLeft":
      e.preventDefault();
      selectAndFocusOption(index - 1);
      break;
    case "Home":
      e.preventDefault();
      selectAndFocusOption(0);
      break;
    case "End":
      e.preventDefault();
      selectAndFocusOption(languages.length - 1);
      break;
    default:
      break;
    }
  };

  const handleConfirm = () => {
    selectedLanguage && persistLanguage(selectedLanguage);
    selectedLanguage && setLanguage(selectedLanguage, selectedLanguage);
    if (isAuthenticated) {
      if (selectedLanguage) {
        const currentUserInfo = useAuthStore.getState().userInfo;
        if (currentUserInfo) {
          useAuthStore.setState({
            userInfo: { ...currentUserInfo, preference_language: selectedLanguage.id }
          });
        }
        savePreferenceLanguage(selectedLanguage.id);
      } else {
        refetchUserInfo();
      }
    }
    close();
  };

  return (
    <Modal open={ open }
      role="dialog"
      aria-labelledby="title-dialog-language"
      sx={ {
        "&.MuiModal-root":{
          alignItems:"center",
          display:"flex",
          justifyContent:"center",
          padding:theme.spacing(2),
          zIndex:99999
        }
      } }
    >

      <StyledModalContent>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          gap={ 2 }
        >
          <Typography
            id="title-dialog-language"
            tabIndex={ 0 }
            color={ theme.customColors.textWidget }
            fontWeight={ 700 }
            sx={ {
              fontSize: "20px"
            } }
          >
            { t("language_select") }
          </Typography>
          <Box
            id={ "close" }
            role="button"
            tabIndex={ 0 }
            aria-label={ t("close") }
            onClick={ () => close() }
            onKeyDown={ (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                close();
              }
            } }
            sx={ {
              display: "flex",
              cursor: "pointer",
              color: theme.customColors.textPrimary,
              "&:focus-visible": {
                outline: `2px solid ${theme.customColors.backgroundPrimaryCta}`,
                outlineOffset: "2px",
                borderRadius: "4px"
              }
            } }
          >
            <X size="24px" />
          </Box>
        </Stack>

        <Stack
          className="scrollable"
          role="radiogroup"
          aria-label={ t("language_select") }
          width="100%"
          gap={ 1.5 }
          sx={ {
            overflowY: "auto"
          } }
        >
          {
            languages.map(({ id, label }, index) => {
              const isSelected = id === selectedLanguage?.id;
              const isTabbable = hasValidSelection ? isSelected : index === 0;

              return (
                <Stack
                  ref={ (el) => { optionRefs.current[index] = el } }
                  role="radio"
                  aria-checked={ isSelected }
                  aria-label={ label || id }
                  tabIndex={ isTabbable ? 0 : -1 }
                  key={ `lang-opt--${id}` }
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={ 2 }
                  onClick={ () => { setSelectedLanguage({ id, label }) } }
                  onKeyDown={ (e) => handleOptionKeyDown(e, index, { id, label }) }
                  sx={ {
                    border: `1px solid ${isSelected ? accentColor : neutralColor}`,
                    borderRadius: "8px",
                    cursor: "pointer",
                    minHeight: "56px",
                    padding: theme.spacing(1.5, 2.5),
                    transition: "border-color 0.25s ease",
                    "&:focus-visible": {
                      outline: "none"
                    }
                  } }
                >
                  <Typography
                    fontWeight={ 700 }
                    lineHeight={ "20px" }
                    sx={ {
                      color: isSelected ? accentColor : neutralColor,
                      transition: "color 0.25s ease"
                    } }
                  >
                    { label || id }
                  </Typography>
                  <Check
                    color={ accentColor }
                    size={ 24 }
                    style={ {
                      flexShrink: 0,
                      opacity: isSelected ? 1 : 0,
                      transition: "opacity 0.25s ease"
                    } }
                  />
                </Stack>
              );
            })
          }
        </Stack>

        <Stack
          direction="row"
          justifyContent="flex-end"
          gap={ 2 }
          width="100%"
        >
          <ButtonDigited
            typology="secondary"
            buttonSize="md"
            onClick={ close }
            text={ t("filters_cancel") }
          />
          <ButtonDigited
            typology="primary"
            buttonSize="md"
            onClick={ handleConfirm }
            text={ t("confirm") }
          />
        </Stack>
      </StyledModalContent>

    </Modal>
  );
}
